//TODO: https://varomoney.atlassian.net/browse/JT-120934
/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Agreement,
  AgreementType,
  ApplicationProductTypeEnum,
  Mutation,
  Query,
  SubmitApplicationResultTypenames,
  UpdateApplicationResultTypenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import { SubmitApplicationArgs } from 'features/application/hooks/useSubmitApplication';
import {
  UpdateApplicationData,
  UpdateApplicationHandler,
} from 'features/application/hooks/useUpdateApplication';
import { LegalAgreementSchema } from 'features/application/screens/LegalAgreements/LegalAgreement.schema';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GroupedAgreements from 'services/graphql/queries/GroupedAgreements';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import SocureDevice from 'shared/utils/socureDeviceSessionId/SocureDeviceSessionId';
import { UseMutationState, useQuery } from 'urql';

import {
  agreementTitles,
  hardware,
  hardwareWeb,
  scope,
} from './legalAgreementsConfig';

const comma = ', ';
const dot = '. ';
const snackbarDuration = 2750;

type LegalAgreementsRoute = '/reactivation/review-info' | '/signup/verify-ssn';

interface AgreementsScreenProps {
  applicationId?: string | null;
  updateApplication: UpdateApplicationHandler;
  updateApplicationResult: UseMutationState<UpdateApplicationData>;
  isUpdateApplicationLoading: boolean;
  onTryAgain: () => void;
  fetching: boolean;
  error?: Error | string | null;
  isSubmitApplicationLoading?: boolean;
  isSubmitApplicationSuccess?: boolean;
  submitApplication: (args: SubmitApplicationArgs) => Promise<void>;
  submitApplicationMutationResult: UseMutationState<Mutation>;
  backRoute: LegalAgreementsRoute;
  isReactivation?: boolean;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
}

const AgreementsScreen = ({
  isSubmitApplicationSuccess,
  applicationId,
  updateApplication,
  updateApplicationResult,
  isUpdateApplicationLoading,
  onTryAgain,
  fetching,
  error,
  isSubmitApplicationLoading = false,
  submitApplication,
  submitApplicationMutationResult,
  backRoute,
  isReactivation = false,
  hasApplicationLoaded,
  isReapplication,
}: AgreementsScreenProps) => {
  const router = useRouter();
  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: 'legal_agreement',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );
  const { bottom } = useSafeAreaInsets();

  const { segmentClient } = useAnalyticsConfiguration();
  const [scrollHeight, setScrollHeight] = useState(0);
  const [isScrolledToBottom, setScrolled] = useState(false);
  const agreeToAllHeightRef = useRef(0);
  const [agreements, setAgreements] = useState<Agreement[]>();
  const [snackbar, setSnackbar] = useState<{
    message: string;
  }>({
    message: '',
  });
  const [{ data }] = useQuery<Query>({
    query: GroupedAgreements,
    requestPolicy: 'cache-and-network',
    variables: {
      types: [
        ApplicationProductTypeEnum.Checking,
        ApplicationProductTypeEnum.Savings,
        ApplicationProductTypeEnum.PartnerCashback,
        ApplicationProductTypeEnum.Advance,
        ApplicationProductTypeEnum.ChargeCard,
        ApplicationProductTypeEnum.PersonalLoan,
      ],
    },
  });
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'legalAgreements',
  });
  const scrollViewRef = useRef<ScrollView | null>(null);

  useEffect(() => {
    setAgreements(data?.groupedAgreements[3]?.agreements);
  }, [data]);

  const [socureDeviceSessionId, setSocureDeviceSessionId] = useState('');

  useEffect(() => {
    const fetchSocureDeviceSessionId = async () => {
      const sessionId = await SocureDevice.getSocureDeviceSessionId();
      setSocureDeviceSessionId(sessionId);
    };
    if (!socureDeviceSessionId) {
      void fetchSocureDeviceSessionId();
    }
  }, [socureDeviceSessionId]);

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      groupAgreement1: false,
      groupAgreement2: false,
      savingsAccountAgreement: false,
      agreeToAll: false,
    },
    resolver: zodResolver(LegalAgreementSchema()),
  });

  const {
    groupAgreement1,
    groupAgreement2,
    savingsAccountAgreement,
    agreeToAll,
  } = getValues();

  useEffect(() => {
    if (
      agreeToAll &&
      [groupAgreement1, groupAgreement2, savingsAccountAgreement].includes(
        false,
      )
    ) {
      reset({
        ...getValues(),
        agreeToAll: false,
      });
    }
    if (groupAgreement1 && groupAgreement2 && savingsAccountAgreement) {
      reset({
        ...getValues(),
        agreeToAll: true,
      });
    }
  }, [
    reset,
    groupAgreement1,
    groupAgreement2,
    savingsAccountAgreement,
    agreeToAll,
    getValues,
  ]);

  useStepStartedFocusAnalytics(
    isSubmitApplicationSuccess
      ? {
          analyticsId: 'completedLegalAgreement',
          customAttributes: {
            step_name: 'legal_agreements',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  useAutoHideSnackbar(
    Boolean(snackbar.message),
    () => {
      setSnackbar({ message: '' });
    },
    snackbarDuration,
  );

  const handleBackButtonAction = useCallback(() => {
    router.replace(backRoute);
  }, [backRoute, router]);

  useBackHandler(handleBackButtonAction);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const onSubmitAgreements = async () => {
    if (applicationId !== null) {
      let products = [
        ApplicationProductTypeEnum.Checking,
        ApplicationProductTypeEnum.Savings,
      ];
      let updatedAgreements = agreements;
      if (!savingsAccountAgreement) {
        products = [ApplicationProductTypeEnum.Checking];
        updatedAgreements = agreements?.filter(
          (a) => a.type !== AgreementType.SavingsAccount,
        );
      }
      const mutationData = await updateApplication({
        updates: {
          productTypes: {
            productSelections: products,
            socureDeviceSessionId,
          },
        },
        agreements: updatedAgreements,
      });
      if (
        mutationData?.updateApplication?.__typename ===
          UpdateApplicationResultTypenames.UpdateApplicationSuccess &&
        agreements?.length
      ) {
        await submitApplication({
          agreements,
          isReapplication,
        });
      }
    }
  };

  useEffect(() => {
    let errorMessage: string | undefined = undefined;
    errorMessage =
      updateApplicationResult.error?.graphQLErrors?.find((err) => err?.message)
        ?.message ??
      submitApplicationMutationResult.error?.graphQLErrors?.find(
        (err) => err?.message,
      )?.message;

    if (
      updateApplicationResult?.data?.updateApplication?.__typename ===
      UpdateApplicationResultTypenames.UpdateApplicationError
    ) {
      errorMessage = updateApplicationResult.data.updateApplication.message;
    }

    if (
      submitApplicationMutationResult?.data?.submitApplication?.__typename ===
      SubmitApplicationResultTypenames.SubmitApplicationError
    ) {
      errorMessage =
        submitApplicationMutationResult.data.submitApplication.message;
    }
    if (errorMessage) {
      setSnackbar({ message: errorMessage });
    }
  }, [updateApplicationResult, submitApplicationMutationResult]);

  const onPressLink = (type: AgreementType) => {
    const url = data?.groupedAgreements[3]?.agreements?.find(
      (e) => e.type === type,
    );
    router.push({
      pathname: '/signup/agreement-viewer',
      params: { pdfUrl: url?.resource?.pdfDocument?.url as string },
    });
  };

  const isScreenLoading =
    fetching || isUpdateApplicationLoading || isSubmitApplicationLoading;

  return (
    <View style={styles.legalContainer}>
      <ScreenTransitionOverlay
        isLoading={isScreenLoading}
        LoadingComponent={
          <View style={styles.loaderContainer}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error && !isScreenLoading ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={onTryAgain}
              screenAnalyticsEventKey="signupFlowError"
            />
          ) : null
        }
      />
      <ScrollView
        ref={scrollViewRef}
        testID="scrollView"
        onScroll={(event) => {
          const contentOffsetY = event.nativeEvent.contentOffset.y;
          const bottomThreshold =
            scrollHeight - event.nativeEvent.layoutMeasurement.height;
          setScrolled(
            isScrolledToBottom
              ? contentOffsetY > bottomThreshold - agreeToAllHeightRef.current
              : contentOffsetY > bottomThreshold,
          );
        }}
      >
        <View style={styles.legalContainer}>
          <Stack.Screen
            options={{
              headerShown: true,
              headerBackVisible: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar
                  {...props}
                  primaryOnPress={handleBackButtonAction}
                />
              ),
            }}
          />
          <View style={styles.citizenshipFormContainer}>
            <TitleLgText
              numberOfLines={0}
              style={[styles.heading, styles.dividerContainer]}
            >
              {translatedText('title')}
            </TitleLgText>

            <BodyBaseText
              numberOfLines={0}
              style={[styles.describeStyle, styles.dividerContainer]}
            >
              {translatedText('description')}
            </BodyBaseText>
            <TitleMdText
              numberOfLines={0}
              style={[styles.heading, styles.dividerContainer]}
            >
              {translatedText('agreementTitle')}
            </TitleMdText>

            <BodyBaseText
              numberOfLines={0}
              style={[styles.describeStyle, styles.dividerContainer]}
            >
              {translatedText('agreementSubject')}
            </BodyBaseText>

            {agreementTitles.map((item) => (
              <View key={item.id}>
                <BodyBaseText
                  numberOfLines={0}
                  style={[styles.describeStyle, styles.dividerContainer]}
                >
                  {translatedText(item.title)}{' '}
                  <BodyBaseText numberOfLines={0}>
                    {translatedText(item.description)}
                  </BodyBaseText>
                </BodyBaseText>
                {item.id === '1' ? (
                  <View style={styles.describeStyle}>
                    {scope.map((item) => (
                      <View key={item.id}>
                        <BodyBaseText
                          numberOfLines={0}
                          style={styles.dividerContainer}
                        >
                          {translatedText(item.title)}
                        </BodyBaseText>
                      </View>
                    ))}
                  </View>
                ) : null}
                {item.id === '5' ? (
                  <View style={styles.describeStyle}>
                    {hardware.map((item) => (
                      <View key={item.id}>
                        <BodyBaseText
                          numberOfLines={0}
                          style={styles.dividerContainer}
                        >
                          {translatedText(item.title)}
                        </BodyBaseText>
                        {item.id === '3' ? (
                          <View style={styles.webPoints}>
                            {hardwareWeb.map((item) => (
                              <View key={item.id}>
                                <BodyBaseText
                                  numberOfLines={0}
                                  style={styles.dividerContainer}
                                >
                                  {translatedText(item.title)}
                                </BodyBaseText>
                              </View>
                            ))}
                          </View>
                        ) : null}
                      </View>
                    ))}
                    <BodyBaseText
                      style={[styles.hardwareStyle, styles.checkBox]}
                      numberOfLines={0}
                    >
                      {translatedText('hardwareFive')}
                    </BodyBaseText>
                  </View>
                ) : null}
              </View>
            ))}

            <View style={styles.describeStyle}>
              <TitleMdText
                numberOfLines={0}
                style={[styles.ssaAgreementTitle, styles.dividerContainer]}
              >
                {translatedText('ssaAgreementTitle')}
              </TitleMdText>
              <BodyBaseText
                numberOfLines={0}
                style={[
                  styles.ssaAgreementDescription,
                  styles.dividerContainer,
                ]}
              >
                {translatedText('ssaAgreementDescription')}
              </BodyBaseText>
              <BodyBaseBoldText
                numberOfLines={0}
                style={[styles.ssaAgreement, styles.dividerContainer]}
              >
                {translatedText('ssaAgreement')}
              </BodyBaseBoldText>
            </View>

            <BodyBaseBoldText
              numberOfLines={0}
              style={[styles.varoBankAccount, styles.dividerContainer]}
            >
              {translatedText('varoBankAccount')}
            </BodyBaseBoldText>

            <BodyBaseText numberOfLines={0} style={styles.dividerContainer}>
              {translatedText('readAndAgree')}
            </BodyBaseText>

            <Controller
              control={control}
              name="groupAgreement1"
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={styles.checkBox}
                  onLayout={(event) => {
                    const layout = event.nativeEvent.layout;
                    setScrollHeight(layout.y);
                  }}
                >
                  <Checkbox
                    labelText=""
                    testId="checkBoxFirst"
                    checked={Boolean(value)}
                    analyticsId="communicationCheckboxClicked"
                    onSelectedStateChange={() => {
                      const nextValue = !value;
                      onChange(nextValue);
                      onBlur();
                      reset({
                        ...getValues(),
                        groupAgreement1: nextValue,
                      });
                    }}
                  />
                  <View style={[styles.hyperLink, styles.describeStyle]}>
                    <BodyBaseText numberOfLines={0}>
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.ElectronicCommunications)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('electronicAgreement')}
                      </HyperlinkBaseText>
                      {comma}
                      <HyperlinkBaseText
                        onPress={() => onPressLink(AgreementType.UserAgreement)}
                        numberOfLines={0}
                      >
                        {translatedText('ccpaNotice')}
                      </HyperlinkBaseText>
                      {comma}
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(
                            AgreementType.OnlineAndMobilePrivacyPolicy,
                          )
                        }
                        numberOfLines={0}
                      >
                        {translatedText('mobilePrivacy')}
                      </HyperlinkBaseText>
                      {comma}
                      {translatedText('includingOur')}
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.ConsumerPrivacyNotice)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('consumerPolicy')}
                      </HyperlinkBaseText>
                      {dot}
                    </BodyBaseText>
                  </View>
                </View>
              )}
            />

            <Controller
              control={control}
              name="groupAgreement2"
              render={({ field: { onChange, onBlur, value } }) => (
                <View style={[styles.checkBox, styles.secondAgreement]}>
                  <Checkbox
                    labelText=""
                    testId="checkBoxSecond"
                    checked={Boolean(value)}
                    analyticsId="generalTermsAndConditionsCheckboxClicked"
                    onSelectedStateChange={() => {
                      const nextValue = !value;
                      onChange(nextValue);
                      onBlur();
                      reset({
                        ...getValues(),
                        groupAgreement2: nextValue,
                      });
                    }}
                  />
                  <View style={[styles.hyperLink, styles.describeStyle]}>
                    <BodyBaseText numberOfLines={0}>
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.GeneralTermsAgreement)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('generalAgreement')}
                      </HyperlinkBaseText>
                      {comma}
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.CheckingAccount)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('bankAgreement')}
                      </HyperlinkBaseText>
                      {comma}
                      {translatedText('and')}
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.CashbackProgramAgreement)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('cashbackProgram')}
                      </HyperlinkBaseText>
                      {dot}
                    </BodyBaseText>
                  </View>
                </View>
              )}
            />

            <TitleSmText numberOfLines={0} style={styles.dividerContainer}>
              {translatedText('savingAccount')}
            </TitleSmText>

            <Controller
              control={control}
              name="savingsAccountAgreement"
              render={({ field: { onChange, onBlur, value } }) => (
                <View style={styles.checkBox}>
                  <Checkbox
                    labelText=""
                    checked={Boolean(value)}
                    analyticsId="savingAccountAgreementCheckboxClicked"
                    onSelectedStateChange={() => {
                      const nextValue = !value;
                      onChange(nextValue);
                      onBlur();
                      reset({
                        ...getValues(),
                        savingsAccountAgreement: nextValue,
                      });
                    }}
                  />
                  <View style={[styles.hyperLink, styles.describeStyle]}>
                    <BodyBaseText numberOfLines={0}>
                      {translatedText('highYieldAccount')}
                      <HyperlinkBaseText
                        onPress={() =>
                          onPressLink(AgreementType.SavingsAccount)
                        }
                        numberOfLines={0}
                      >
                        {translatedText('savingAgreement')}
                      </HyperlinkBaseText>
                      {dot}
                    </BodyBaseText>
                  </View>
                </View>
              )}
            />

            <View style={styles.applyDisclosureDivider}>
              <HairLineDivider />
            </View>

            <BodyBaseText numberOfLines={0} style={styles.applyDisclosure}>
              {translatedText('applyDisclosure')}
            </BodyBaseText>
          </View>
        </View>
      </ScrollView>

      <HairLineDivider />
      {isScrolledToBottom ? (
        <Controller
          control={control}
          name="agreeToAll"
          render={({ field: { onChange, onBlur, value } }) => (
            <View
              style={[styles.checkBox, styles.agreeToAllContainer]}
              onLayout={(event) => {
                agreeToAllHeightRef.current = event.nativeEvent.layout.height;
              }}
            >
              <Checkbox
                labelText={translatedText('agreeToAll')}
                testId="checkBoxAll"
                checked={Boolean(value)}
                analyticsId="agreeToAllClicked"
                customAttributes={{
                  ...(isReactivation && {
                    reactivation_flow: 'true',
                    reactivation_id: applicationId,
                  }),
                  ...(isReapplication && {
                    reapplication_flow: 'true',
                  }),
                }}
                onSelectedStateChange={() => {
                  const nextValue = !value;
                  onChange(nextValue);
                  onBlur();
                  reset({
                    groupAgreement1: nextValue,
                    groupAgreement2: nextValue,
                    savingsAccountAgreement: nextValue,
                    agreeToAll: nextValue,
                  });
                }}
              />
            </View>
          )}
        />
      ) : null}
      <View
        style={[
          styles.buttonContainer,
          { paddingBottom: bottom === 0 ? theme.spacing.m : bottom },
        ]}
      >
        <Button
          label={
            isScrolledToBottom
              ? translatedText('apply')
              : translatedText('review')
          }
          size="LARGE"
          type="PRIMARY"
          analyticsId={isScrolledToBottom ? 'applyClicked' : 'reviewClicked'}
          customAttributes={{
            ...(isReactivation && {
              reactivation_flow: 'true',
              reactivation_id: applicationId,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
            ...(segmentClient.getMarketingSessionData('roktClickId') && {
              passback_conversion_tracking_id:
                segmentClient.getMarketingSessionData('roktClickId'),
            }),
          }}
          isLoading={isScreenLoading}
          onPress={async () => {
            if (!isScrolledToBottom) {
              scrollViewRef?.current?.scrollTo({
                y: scrollHeight,
                animated: true,
              });
            } else {
              await onSubmitAgreements();
            }
          }}
          isDisabled={isScrolledToBottom && !isValid}
          width="FULL"
        />
      </View>
      {snackbar.message ? (
        <Snackbar duration={snackbarDuration} text={snackbar.message} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  agreeToAllContainer: {
    justifyContent: 'flex-start',
    paddingTop: theme.spacing.m,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    margin: theme.spacing.m,
  },
  checkBox: {
    backgroundColor: theme.color.background.primary,
    flexDirection: 'row',
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
  },
  citizenshipFormContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    paddingTop: theme.spacing.m,
  },
  describeStyle: {
    paddingBottom: theme.spacing.m,
  },
  dividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  hardwareStyle: {
    marginTop: theme.spacing.m,
  },
  heading: {
    marginBottom: theme.spacing.m,
  },
  hyperLink: {
    flex: 1,
    marginLeft: theme.spacing.m,
    marginRight: theme.spacing.s,
    marginTop: theme.spacing.s,
  },
  legalContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    flexGrow: 1,
    justifyContent: 'center',
  },
  ssaAgreement: {
    marginTop: theme.spacing.m,
  },
  secondAgreement: {
    paddingTop: theme.spacing.s,
  },
  ssaAgreementDescription: {
    marginTop: theme.spacing.s,
  },
  ssaAgreementTitle: {
    marginTop: theme.spacing.m,
  },
  varoBankAccount: {
    marginTop: theme.spacing.m,
  },
  applyDisclosure: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  applyDisclosureDivider: {
    marginTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  webPoints: {
    marginLeft: theme.spacing.xl,
  },
});

export default AgreementsScreen;
